"use client";

// useGatedAction — wraps any action behind sign-in.
//
// Usage:
//   const triggerKininaru = useGatedAction(() => submitKininaru(id));
//   <button onClick={triggerKininaru}>👀 気になる</button>
//
// Behavior:
//   - Signed in  → calls action immediately
//   - Signed out → opens Clerk sign-in modal, stores intent
//                  when sign-in completes and isSignedIn flips true,
//                  the pending action fires automatically (same page context)

import { useEffect, useRef, useCallback } from "react";
import { useUser, useClerk } from "@clerk/nextjs";

export function useGatedAction(action: () => void): () => void {
  const { isSignedIn, isLoaded } = useUser();
  const { openSignIn } = useClerk();

  const pendingRef = useRef(false);
  const actionRef = useRef(action);

  // Keep actionRef up-to-date without causing effect re-runs
  useEffect(() => {
    actionRef.current = action;
  });

  // When sign-in completes, fire the pending action if one was stored
  useEffect(() => {
    if (isSignedIn && pendingRef.current) {
      pendingRef.current = false;
      actionRef.current();
    }
  }, [isSignedIn]);

  return useCallback(() => {
    if (!isLoaded) return;
    if (isSignedIn) {
      action();
    } else {
      pendingRef.current = true;
      openSignIn(); // modal — page context is preserved
    }
  }, [isLoaded, isSignedIn, action, openSignIn]);
}
